import * as React from 'react';
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';

// Utils
import { formatCents } from '@src/shared/src/util/general';
import useClickOutside from '@toolkit/util/useClickOutside';

// Constants
import {
  ENVIRONMENT,
  CENTS_CONVERSION_TYPES,
  ACCOMMODATION_TYPES,
} from '@src/shared/src/const/app';
import { t } from '@toolkit/util/i18n';
// Actions, Models & Interfaces
import { HotelModel } from '@src/shared/src/models';
// Components
import HotelTitle from './HotelTitle';
import { HotelRating } from './HotelRating';
import { HotelPrice } from './HotelPrice';
import { Airbnb } from '@toolkit/ui/icons/Airbnb';
// Styles
import '../styles/HotelMapMarker.scss';
import HotelMainImage from './HotelMainImage';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { IRootState } from '@src/store/root.reducer';
import { differenceInDays } from 'date-fns';

type Props = {
  hotel: HotelModel;
  onHandelTooltip?: (hotelId: string) => void;
  onShowDetails?: (hotelId: string) => void;
  isTooltipOpen?: boolean;
};

const HotelMapMarker: React.FC<Props> = ({
  hotel,
  onShowDetails,
  isTooltipOpen,
  onHandelTooltip,
}) => {
  const closeTooltip = () => onShowDetails && onHandelTooltip?.(hotel.id);
  const outsideRef = useClickOutside(() => closeTooltip());
  const search = useSelector((state: IRootState) => state.search.currentSearch);

  const handleInfoWindowClick = (event) => {
    event.stopPropagation();
    onShowDetails(hotel.id);
  };

  const cheapestPrice = Math.min(...hotel.fares.map((fare) => fare.price));
  const nights = differenceInDays(search.arrAt, search.depAt);
  const cheapestPricePerNight = cheapestPrice / nights;

  return (
    <>
      <AdvancedMarker
        collisionBehavior="OPTIONAL_AND_HIDES_LOWER_PRIORITY"
        position={{ lat: hotel.latitude, lng: hotel.longitude }}
        className={classNames('hotel-map-marker-tag', {
          'hotel-map-marker-tag-airbnb':
            hotel.accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL,
        })}
        onClick={() => onHandelTooltip?.(hotel.id)}>
        <span data-rh={hotel.isMonthlyEstimate ? t('hotelPrice.tooltip.monthlyEstimate') : undefined}>
          {hotel.isMonthlyEstimate ? '~ ' : ''}
          {formatCents(cheapestPricePerNight, CENTS_CONVERSION_TYPES.FLOOR)}
        </span>
      </AdvancedMarker>

      {isTooltipOpen && (
        <InfoWindow
          position={{ lat: hotel.latitude, lng: hotel.longitude }}
          zIndex={1}
          minWidth={360}
          onClose={() => closeTooltip()}>
          <div
            className="hotel-map-marker"
            ref={outsideRef}
            onClick={(event) => handleInfoWindowClick(event)}>
            <div className="hotel-map-marker-image">
              <HotelMainImage
                primaryImageURL={hotel.primaryPictureURL}
                pictureURLs={hotel.pictureURLs}
              />
            </div>
            <div className="hotel-map-marker-inner">
              <HotelTitle hotel={hotel} />
              <div className="hotel-map-marker-row">
                {hotel.accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL && (
                  <div className="tcp-hotel-airbnb">
                    <Airbnb />
                  </div>
                )}
                <HotelRating
                  numReviews={hotel.numReviews}
                  rating={hotel.originalUserRating}
                  scale={hotel.originalUserRatingScale}
                />
                <div className="hotel-map-marker-price">
                  <HotelPrice
                    price={cheapestPrice}
                    env={ENVIRONMENT.SEARCH}
                    pricePerNight={cheapestPricePerNight}
                    accommodationType={hotel.accommodationType}
                    currencyChanged={hotel.currency !== hotel.originalCurrency}
                    negotiatedRates={hotel.negotiatedRates}
                    orgFavorited={hotel.orgFavorited}
                    co2InKg={hotel.co2InKg}
                    isMonthlyEstimate={hotel.isMonthlyEstimate}
                    monthlyPrice={hotel.monthlyPrice}
                  />
                </div>
              </div>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default HotelMapMarker;
